import { Text, TextStyle } from "pixi.js";
import type { LetterForm, WordText } from "@talent/tasks";
import { fa } from "./format.js";

/**
 * Persian numerals, arithmetic signs and single letters, on the game canvas.
 *
 * **This file breaks the oldest rule in the codebase, and only where the rule does not apply.**
 * Every stimulus before it was text-free by policy (v2 §15): reading ability must not leak into a
 * measure of something else, so nothing on a canvas was ever a glyph — the sequence game draws an
 * empty well rather than a question mark, and the maze's arrows are paths rather than chevrons.
 *
 * The three games that use this are different in kind. The numeral IS the thing being taught in
 * arithmetic and in sudoku's digit levels; the letter IS the thing being taught in the alphabet
 * game. Refusing to draw them would mean refusing to teach any of the three (R-6). All three
 * declare `languageIndependentStimulus: false`, which is the field that marks the confound for
 * whoever reads their data later, and a fourth one fails a test in `packages/tasks` on purpose.
 *
 * **Nothing here accepts a sentence, and that is enforced rather than requested.** Digits arrive
 * as numbers, signs as keys of a fixed table, and letters as `LetterForm` — a branded type only
 * `@talent/tasks` can mint, one shaped letter at a time. There is no entry point taking a string,
 * so the door this file opens in the no-language rule is exactly one glyph wide.
 *
 * Digits come from `fa()` so there is one definition of what a Persian numeral looks like, shared
 * with every date and count in the DOM.
 */

/**
 * The signs an arithmetic question needs.
 *
 * Written as an object rather than passed as raw strings, so nothing else can start drawing arbitrary
 * text through this module — the one property that keeps "canvas is text-free" true everywhere else.
 * Persian uses the same four operators as Latin typesetting.
 */
export const SIGN = {
  plus: "+",
  minus: "−",
  times: "×",
  dividedBy: "÷",
  equals: "=",
} as const;
/**
 * There is no question mark here, and its absence is deliberate twice over.
 *
 * A question needs no marker: the answer tiles below it are what make it a question, exactly as the
 * sequence game's empty well needs no `?`. And the Persian question mark is an Arabic-script
 * character, so having one in this file would trip `check-copy.ts` — which is the guard saying,
 * correctly, that a glyph belongs in the copy table or nowhere.
 */

export type Sign = keyof typeof SIGN;

const FAMILY = "Vazirmatn";

let fontReady: Promise<void> | null = null;

/**
 * Waits for Vazirmatn to be usable, once per page.
 *
 * **Not optional, and the failure it prevents is invisible.** Pixi rasterises a `Text` when it is
 * created and caches the texture. Create one before the webfont has loaded and it is drawn in the
 * fallback face and stays that way — Persian digits rendered in whatever the system had, frozen for
 * the rest of the sitting, with nothing in the console to say so.
 *
 * Resolves rather than rejects when the font cannot be loaded at all. A child on a bad connection
 * should get a game in a fallback face, not a game that refuses to open.
 */
export function ensureGlyphFont(): Promise<void> {
  if (fontReady) return fontReady;
  fontReady = (async () => {
    try {
      // A weight and size are required by the CSS Font Loading API's shorthand parser; both are
      // arbitrary here, since the variable font covers the whole range in one file.
      await document.fonts.load(`700 48px "${FAMILY}"`);
    } catch {
      /* A fallback face is a worse-looking game. No face at all is no game. */
    }
  })();
  return fontReady;
}

interface GlyphOptions {
  size: number;
  color: number;
  /** Bold by default: a numeral on a dark stage needs the weight to hold its counters. */
  weight?: "600" | "700" | "800";
}

function style(opts: GlyphOptions): TextStyle {
  return new TextStyle({
    fontFamily: FAMILY,
    fontSize: opts.size,
    fontWeight: opts.weight ?? "700",
    fill: opts.color,
    // The glyphs are drawn one at a time and centred by their own anchor, so no wrapping, no
    // alignment and no direction handling is needed — which is also why this module never accepts a
    // sentence.
    align: "center",
  });
}

/**
 * Fits a glyph inside a square, on its INK rather than on its line box.
 *
 * (No example letters in these comments, deliberately: `check-copy.ts` refuses Persian script anywhere
 * under `src/` outside `copy.ts`, comments included, and it is right to — a glyph in a comment is one
 * edit away from being a glyph on a screen. This note is here because that rule has now been broken
 * twice in this same file, both times while explaining a genuinely typographic point.)
 *
 * **Two bugs in one, and both were visible on a phone before they were visible in the code.**
 *
 * *The letter sat low and its dots ran off the card.* Pixi anchors a `Text` on the font's line box —
 * ascent plus descent, the same tall box for every glyph — so a short letter renders in the middle
 * of a box that is mostly empty above it, and the marks below it end up near the bottom edge. A
 * letter whose dots sit underneath had them run over the edge of the card.
 *
 * *The guard that was supposed to catch that only measured width.* A tall or low glyph was never
 * scaled at all, because nothing looked at its height.
 *
 * Centring on the ink was rejected when this was first written, on the reasoning that a shared
 * baseline is information — a descender hangs below the line where a flat letter sits on it, and
 * normalising each glyph would delete that. The reasoning was wrong because the premise was:
 * **there is no shared baseline.
 * Every glyph is alone on its own card**, so there is nothing to read its position against, and all
 * the line box contributes is empty space and a clipped letter.
 *
 * Measured with the platform's own text metrics rather than Pixi's, because `actualBoundingBox*` is
 * the ink and Pixi's bounds are the box. Falls back to leaving the glyph alone if the browser will
 * not report ink extents — a slightly off-centre letter beats no letter.
 */
let inkRuler: CanvasRenderingContext2D | null | undefined;

export function fitGlyph(glyph: Text, text: string, size: number, room: number): void {
  if (inkRuler === undefined) inkRuler = document.createElement("canvas").getContext("2d");
  if (!inkRuler) return;

  inkRuler.font = `${glyph.style.fontWeight} ${size}px "${FAMILY}"`;
  inkRuler.textAlign = "left";
  inkRuler.textBaseline = "alphabetic";
  const m = inkRuler.measureText(text);

  const inkWidth = m.actualBoundingBoxLeft + m.actualBoundingBoxRight;
  const inkHeight = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
  if (!(inkWidth > 0) || !(inkHeight > 0)) return;

  // Only ever shrinks. Scaling a small glyph up would make its size mean something, and "tap the
  // biggest one" is a rule a child finds before they find the letter.
  const scale = Math.min(1, room / inkWidth, room / inkHeight);
  glyph.scale.set(scale);

  /**
   * Where the ink centre sits relative to the box centre Pixi placed the glyph on, in the glyph's
   * own units — then moved by that much, the other way, so the ink lands on the card's middle.
   */
  const boxCentreY = (m.fontBoundingBoxDescent - m.fontBoundingBoxAscent) / 2;
  const inkCentreY = (m.actualBoundingBoxDescent - m.actualBoundingBoxAscent) / 2;
  const inkCentreX = (m.actualBoundingBoxRight - m.actualBoundingBoxLeft) / 2;
  glyph.position.set(
    (m.width / 2 - inkCentreX) * scale,
    (boxCentreY - inkCentreY) * scale,
  );
}

/**
 * A number, as Persian digits, centred on its own position.
 *
 * `anchor` at the middle rather than the top-left because every caller places these at the centre of
 * a tile. Anchoring at a corner and then subtracting half the measured width is the same arithmetic
 * done later, in each caller, differently.
 */
export function numberGlyph(value: number, opts: GlyphOptions): Text {
  const text = new Text({ text: fa(value), style: style(opts) });
  text.anchor.set(0.5);
  // The rasterised texture is the resolution it is created at, so a retina screen needs to be asked
  // for. Capped at 2: past that it is memory for no visible gain on any phone.
  text.resolution = Math.min(window.devicePixelRatio || 1, 2);
  return text;
}

/** One arithmetic sign, centred the same way. */
export function signGlyph(sign: Sign, opts: GlyphOptions): Text {
  const text = new Text({ text: SIGN[sign], style: style(opts) });
  text.anchor.set(0.5);
  text.resolution = Math.min(window.devicePixelRatio || 1, 2);
  return text;
}

/**
 * One Persian letter, in the shape it wears where it is standing.
 *
 * The argument is a `LetterForm`, which is a string that only `@talent/tasks` can produce. That is
 * the whole access-control story for text on the canvas: this function cannot be called with a
 * word, because a word cannot be turned into a `LetterForm` from here.
 *
 * **Centred on the font's box, not on the ink, and the difference matters.** Anchoring at 0.5 puts
 * every card's letter on the same baseline, so a descender hangs below the line and a letter that
 * sits on it sits on it — exactly as they do in a word. Normalising each glyph to fill its card would
 * look tidier and would delete the vertical position, which for several pairs is most of what tells
 * them apart.
 *
 * (Written without example letters on purpose: `check-copy.ts` refuses Persian script anywhere outside
 * `copy.ts`, comments included, and it is right to — a glyph in a comment is one edit away from being
 * a glyph on a screen.)
 */
export function letterGlyph(form: LetterForm, opts: GlyphOptions): Text {
  const text = new Text({ text: form, style: style(opts) });
  text.anchor.set(0.5);
  text.resolution = Math.min(window.devicePixelRatio || 1, 2);
  return text;
}

/**
 * One word from the bundle's vocabulary, shaped and joined by the browser.
 *
 * **This is the widest the no-language rule has ever been opened, and it is opened onto a list.**
 * `WordText` is branded like `LetterForm`, so the only strings that can arrive here are the ones
 * `words.ts` holds — a closed vocabulary versioned and pinned with the content bundle. There is
 * still no way to draw a sentence, or a name, or anything a component composed at runtime.
 *
 * No positional-form handling and none needed: a whole word is shaped correctly by the text engine
 * on its own, which is exactly the case the letters game could NOT rely on — a lone letter asking
 * for a joined form is the one that needs help.
 */
export function wordGlyph(word: WordText, opts: GlyphOptions): Text {
  const text = new Text({ text: word, style: style(opts) });
  text.anchor.set(0.5);
  text.resolution = Math.min(window.devicePixelRatio || 1, 2);
  return text;
}
